// ============================================================================
// Home — three numbers, then the work by urgency (item #650, TCSD-0189/0366).
//
// Top to bottom, exactly as the design the account managers validated:
// search · the view chips and Compact / Comfortable · not sent to Jose ·
// starting this week · open complaints · Done today · Overdue · Today ·
// This week · the Ask AI bar pinned above the tab bar.
//
// ⛔ No tile grid, no map, no vehicle card. The map lives under My Route; the
// vehicle check is a row while it is open. ⛔ No Done control on a row: a row
// leaves by Send to… or Clear (item #835), each with Undo in Done today.
// ============================================================================
import { useCallback, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Sparkles } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import { errorMessage } from '../../shared/errors';
import { refreshReaders } from '../../shared/save-path';
import { useWorkScope, WorkScopeChips } from '../../shared/scope';
import { updateProjectStage } from '../project-work/hooks/use-project-data';
import { useHomeWork } from './use-home-work';
import { byBand, focusElementId, focusWhenReady, type WorkRow } from './home-work';
import { useDensity, useDoneToday, type DoneItem } from './done-today';
import { BandList, DoneTodayStrip } from './work-list';
import { useRowActions } from './row-actions';
import { TileSheet, type TileKind } from './tile-sheet';

const money = (n: number) => `$${Math.round(n).toLocaleString('en-US')}`;

export default function HomePage() {
  const navigate = useNavigate();
  const { employee } = useAuth();
  const eid = employee?.employee_id ?? null;
  const ws = useWorkScope();
  const { rows, tiles, loading, error, reload } = useHomeWork(ws);
  const [density, setDensity] = useDensity(eid);
  const { done, opened, forget, add } = useDoneToday(eid, rows, loading);
  const [toast, setToast] = useState<string | null>(null);
  const [sheet, setSheet] = useState<TileKind | null>(null);
  const [ask, setAsk] = useState('');
  const bands = byBand(rows);

  const say = useCallback((m: string) => {
    setToast(m);
    window.setTimeout(() => setToast(null), 3200);
  }, []);
  const actions = useRowActions({ eid, add, forget, reload, say });

  const open = useCallback(
    (r: WorkRow) => {
      opened(r);
      navigate(r.link);
      focusWhenReady(focusElementId(r.focusKey));
    },
    [navigate, opened],
  );

  const undo = async (d: DoneItem) => {
    if (await actions.undo(d)) return;
    if (!d.projectId || !eid) return;
    try {
      await updateProjectStage(d.projectId, 'Draft', eid);
      forget(d.key);
      refreshReaders(['projects', 'notification_recipients']);
      reload();
      say('Back to Draft. Jose’s “ready to invoice” task for it was closed.');
    } catch (e) {
      say(errorMessage(e, 'That card could not be put back.'));
    }
  };

  const submitAsk = (e: FormEvent) => {
    e.preventDefault();
    const text = ask.trim();
    if (!text) return;
    navigate('/copilot', { state: { prompt: text } });
  };

  const empty = !loading && rows.length === 0;

  return (
    <div className="min-h-full bg-tcs-bg-muted">
      <div className="mx-auto max-w-xl px-4 pb-28 pt-3 md:pb-24">
        <button
          type="button"
          onClick={() => window.dispatchEvent(new Event('tcs:open-palette'))}
          className="flex min-h-[44px] w-full items-center gap-2 rounded-xl border border-tcs-border bg-tcs-bg px-3 text-left text-[14px] text-tcs-text-muted"
        >
          <Search className="h-4 w-4" /> Search accounts and facilities
        </button>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          <WorkScopeChips scope={ws.scope} options={ws.options} onChange={ws.setScope} />
          <span className="flex-1" />
          <span className="inline-flex rounded-full border border-tcs-border bg-tcs-bg p-0.5" role="group" aria-label="Row size">
            {(['compact', 'comfortable'] as const).map((d) => (
              <button
                key={d}
                type="button"
                aria-pressed={density === d}
                onClick={() => setDensity(d)}
                className={`min-h-[32px] rounded-full px-2.5 text-[11.5px] ${density === d ? 'bg-tcs-inverse text-tcs-inverse-ink' : 'text-tcs-text-secondary'}`}
              >
                {d === 'compact' ? 'Compact' : 'Comfortable'}
              </button>
            ))}
          </span>
        </div>

        <div className="mt-2.5 grid grid-cols-3 gap-2">
          {(
            [
              ['not_sent', tiles ? money(tiles.notSentTotal) : '—', 'not sent to Jose'],
              ['starts', tiles ? String(tiles.startingThisWeek) : '—', 'starting this week'],
              ['complaints', tiles ? String(tiles.openComplaints) : '—', 'open complaints'],
            ] as const
          ).map(([k, v, l]) => (
            <button
              key={k}
              type="button"
              onClick={() => (k === 'not_sent' ? navigate('/project-work') : setSheet(k))}
              className="min-h-[56px] rounded-xl border border-tcs-border bg-tcs-bg px-2.5 py-2 text-left"
            >
              <span className="block text-[19px] font-bold tabular-nums text-tcs-text">{v}</span>
              <span className="block text-[11px] text-tcs-text-secondary">{l}</span>
            </button>
          ))}
        </div>

        <DoneTodayStrip done={done} onUndo={(d) => void undo(d)} />

        {error && <p className="mt-3 rounded-md bg-st-danger-bg px-3 py-2 text-[13px] text-st-danger-fg">{error}</p>}
        {loading && rows.length === 0 && <p className="mt-6 text-center text-[13px] text-tcs-text-muted">Loading your work…</p>}
        <BandList band="overdue" rows={bands.overdue} density={density} onOpen={open} onSendTo={actions.onSendTo} onClear={actions.onClear} />
        <BandList band="today" rows={bands.today} density={density} onOpen={open} onSendTo={actions.onSendTo} onClear={actions.onClear} />
        <BandList band="this_week" rows={bands.this_week} density={density} onOpen={open} onSendTo={actions.onSendTo} onClear={actions.onClear} />
        {empty && <p className="mt-6 text-center text-[13px] text-tcs-text-muted">Nothing waiting on you in this view.</p>}
      </div>

      <form
        onSubmit={submitAsk}
        className="fixed inset-x-3 bottom-[calc(72px+env(safe-area-inset-bottom))] z-20 mx-auto flex max-w-xl items-center gap-2 rounded-2xl border border-tcs-border bg-tcs-bg px-3 shadow-lg md:sticky md:inset-x-auto md:bottom-4 md:mt-4"
      >
        <Sparkles className="h-4 w-4 shrink-0 text-tcs-accent-ink" />
        <input
          id="home-ask-ai"
          value={ask}
          onChange={(e) => setAsk(e.target.value)}
          placeholder={'Tell me what you need — "change the sub at Lee\'s Summit"'}
          aria-label="Ask AI"
          className="min-h-[46px] min-w-0 flex-1 bg-transparent text-[16px] text-tcs-text placeholder:text-tcs-text-muted focus:outline-none"
        />
        <button type="submit" className="min-h-[36px] rounded-lg bg-tcs-accent-ink px-3 text-[13px] font-semibold text-tcs-accent-on">
          Ask
        </button>
      </form>

      {actions.sheets}
      {sheet && ws.scope && <TileSheet kind={sheet} scope={ws.scope} facilityIds={ws.facilityIds ?? null} onClose={() => setSheet(null)} />}
      {toast && (
        <div role="status" className="fixed inset-x-4 top-16 z-[60] mx-auto max-w-md rounded-xl bg-tcs-inverse px-3 py-2.5 text-[13px] text-tcs-inverse-ink shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}
